import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as EditIcon } from '../../../assets/svg/editIcon.svg';
import { TrashIcon } from '../../svgs';
import Field from '../Field/Field';
import BtButton from '../../../ts-components/bt-button/bt-button';
import styles from './ContactRow.module.scss';

type Contact = {
  _id?: string;
  name?: string;
  email?: string;
  title?: string;
  phone?: string;
};

type Props = {
  contact: Contact;
  index: number;
  isEditing?: boolean;
  canEdit?: boolean;
  canRemove?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  onRemove?: () => void;
  onChange?: (field: keyof Contact, value: string) => void;
  profileInfo: any;
  errorMessages?: Partial<Record<keyof Contact, string>>;
  onSave?: (contact: Contact) => Promise<void> | void;
  hoverActions?: React.ReactNode;
};

const ContactRow: React.FC<Props> = ({
  contact,
  index,
  isEditing = false,
  canEdit = true,
  canRemove = true,
  onEdit,
  onSave,
  onCancel,
  onRemove,
  onChange,
  profileInfo,
  errorMessages,
  hoverActions = null,
}) => {
  const [t] = useTranslation('supplierDatabase');
  const [isHovered, setIsHovered] = useState(false);
  const [editedContact, setEditedContact] = useState<Contact>(contact);
  const [isSaving, setIsSaving] = useState(false);

  // Reset edited contact when component stops being edited or contact prop changes
  useEffect(() => {
    if (!isEditing) {
      setEditedContact(contact);
    }
  }, [isEditing, contact]);

  const handleFieldChange = (field: keyof Contact, value: string) => {
    const updated = { ...editedContact, [field]: value };
    setEditedContact(updated);
    if (onChange) {
      onChange(field, value);
    }
  };

  const handleSave = async () => {
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave(editedContact);
      } catch (error) {
        console.error('Failed to save:', error);
      } finally {
        setTimeout(() => {
          setIsSaving(false);
        }, 500);
      }
    }
  };

  const handleCancel = () => {
    setEditedContact(contact);
    if (onCancel) {
      onCancel();
    }
  };

  if (isEditing) {
    return (
      <div className={`${styles['contact-row']} ${styles['contact-row--editing']}`}>
        <div className={styles['contact-row__fields']}>
          <Field
            placeholder={t('supplierDetailPanel.profileInfo.diff.label.name', 'Namn')}
            value={editedContact.name || ''}
            onChange={(value) => handleFieldChange('name', value)}
            errorMessage={errorMessages?.name}
            required={true}
          />
          <Field
            placeholder={t('supplierDetailPanel.profileInfo.diff.label.email', 'E-post')}
            value={editedContact.email || ''}
            onChange={(value) => handleFieldChange('email', value)}
            errorMessage={errorMessages?.email}
            required={true}
          />
          <Field
            placeholder={t('supplierDetailPanel.profileInfo.diff.label.title', 'Titel')}
            value={editedContact.title || ''}
            onChange={(value) => handleFieldChange('title', value)}
            errorMessage={errorMessages?.title}
          />
          <Field
            placeholder={t('supplierDetailPanel.profileInfo.diff.label.phone', 'Telefon')}
            value={editedContact.phone || ''}
            onChange={(value) => handleFieldChange('phone', value)}
            errorMessage={errorMessages?.phone}
          />
        </div>
        <div className={styles['contact-row__actions']}>
          <BtButton
            color="white"
            size="xxs"
            onClick={handleSave}
            disabled={isSaving || !editedContact.name?.trim() || !editedContact.email?.trim()}
            loaderShow={isSaving}
          >
            {t('button.save', 'Spara')}
          </BtButton>
          <BtButton
            color="white"
            size="xxs"
            onClick={handleCancel}
            disabled={isSaving}
          >
            {t('button.cancel', 'Avbryt')}
          </BtButton>

        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles['contact-row']} ${!canEdit && !hoverActions ? styles['disabled'] : ''}`}
      key={index}
      onMouseEnter={canEdit || hoverActions ? () => setIsHovered(true) : undefined}
      onMouseLeave={canEdit || hoverActions ? () => setIsHovered(false) : undefined}
    >
      <div className={styles['contact-row__with-profile-info']}>
        {profileInfo ?? <div style={{ width: '18px' }}></div>}
        <div className={styles['contact-row__name-location']}>
          <span className={styles['contact-row__name']}>{contact.name || ''}</span>
          <span className={styles['contact-row__location']}>{contact.title || ''}</span>
        </div>
      </div>
      {!isHovered && (<div className={styles['contact-row__email-phone']}>
        <span className={styles['contact-row__email']}>{contact.email || ''}</span>
        <span className={styles['contact-row__phone']}>{contact.phone || ''}</span>
      </div>)}
      {hoverActions ?
        (isHovered ? hoverActions : null) :
        (isHovered && canEdit && (
          <div className={styles['contact-row__actions']}>
            {onEdit && (
              <div className={styles['contact-row__edit-icon']} onClick={onEdit}>
                <EditIcon />
              </div>
            )}
            {canRemove && onRemove && (
              <div className={styles['contact-row__remove-icon']} onClick={onRemove}>
                <TrashIcon />
              </div>
            )}
          </div>
        ))}
    </div>
  );
};

export default ContactRow;

